import React, {CSSProperties} from "react";
import {Place} from "@game/map/place";
import {LETTER_THEME, TILE_SIZE} from "@components/core/theme";
import {Tile} from "@game/map/tile";
import {IDebug} from "@redux/slices/debug";
import {ShovelIcon} from "@components/interface/ShovelIcon";

export const BONUS_COLOURS: Record<number, string> = {
    1: "#8b6914",  // amber — from palette
    2: "#2d5a1b",  // deep forest green — from palette
    3: "#c84b22",  // terracotta
    4: "#2b4878",  // cartographic ink blue
    5: "#7a1f2e"   // aged burgundy
}

const styles: Record<string, CSSProperties> = {
    block: {
        backgroundColor: "#b4a898",
        borderTop:    "1px solid rgba(60,40,20,0.3)",
        borderLeft:   "1px solid rgba(60,40,20,0.3)",
        borderBottom: "3px solid rgba(60,40,20,0.6)",
        borderRight:  "3px solid rgba(60,40,20,0.6)",
        borderRadius: 3,
        display: "flex",
        flexGrow: 1,
        margin: 2,
        userSelect: "none"
    },
    bonus: {
        border: "2px dotted white",
        color: "white",
        display: "flex",
        flexGrow: 1,
        marginRight: 1,
        marginBottom: 1,
        padding: 0,
        position: "relative",
        userSelect: "none"
    },
    bonusNumber: {
        ...LETTER_THEME.bonus
    },
    decoration: {
        backgroundColor: "lightgreen",
        border: "2px dotted darkgreen",
        flexGrow: 1,
        margin: 4,
        userSelect: "none"
    },
    road: {
        backgroundColor: "lightgrey",
        border: "2px dotted darkgrey",
        flexGrow: 1,
        margin: 4,
        userSelect: "none"
    },
    advert: {
        backgroundColor: "#8a8a8a",
        borderTop:    "1px solid rgba(255,255,255,0.2)",
        borderLeft:   "1px solid rgba(255,255,255,0.2)",
        borderBottom: "3px solid rgba(0,0,0,0.4)",
        borderRight:  "3px solid rgba(0,0,0,0.4)",
        borderRadius: 3,
        display: "flex",
        flexGrow: 1,
        margin: 2,
        userSelect: "none"
    },
    shovelButton: {
        alignItems: "center",
        display: "flex",
        height: "100%",
        justifyContent: "flex-start",
        position: "relative",
        width: "100%"
    },
    shovelIcon: {
        height: "100%",
        width: "70%",
        flexShrink: 0
    },
    shovelText: {
        color: "white",
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontWeight: "bold",
        fontSize: 15,
        position: "absolute",
        bottom: 16,
        right: 4,
        userSelect: "none" as const
    },
    placeImage: {
        width: TILE_SIZE.x,
        height: TILE_SIZE.y,
        objectFit: "contain" as const,
        position: "absolute" as const,
        top: 0,
        left: 0,
        pointerEvents: "none" as const
    }
};

interface IPlaceView {
    debug?: IDebug
    tile: Tile
}

export function PlaceView({debug, tile}: IPlaceView) {
    const place = tile.place!;
    const containerStyle = {
        ...styles[place.type],
        ...place.bonus ? {
            ...styles.bonus,
            backgroundColor: BONUS_COLOURS[place.bonus]
        } : {}
    };

    return (
        <div style={containerStyle}>
            {place.type === "bonus" &&
                <div style={styles.shovelButton}>
                    <ShovelIcon background={BONUS_COLOURS[place.bonus!]} style={styles.shovelIcon}/>
                    {place.bonus && <span style={styles.shovelText}>+{place.bonus}</span>}
                </div>
            }
            {debug?.showTileImage && tile.placeImage &&
                <img alt="place" src={`/images/places/${tile.placeImage}.png`} style={styles.placeImage}
                     draggable={false}/>
            }
        </div>
    );
}
